/**
 * i18n bootstrap — react-i18next with browser-language detection.
 *
 * Strategy:
 *   • Translation files live under src/i18n/locales/<lang>/<namespace>.json
 *   • Default namespace = 'common'; portal-specific keys live under 'student',
 *     'parent', 'admin' so we can lazy-split later if bundle size matters.
 *   • Detected language order: localStorage → cookie → navigator → fallback (en).
 *   • Falls back gracefully — a missing key surfaces the key itself so it's
 *     obvious in dev which strings still need translating.
 *
 * Languages shipped today: English, Shona (sn), Ndebele (nd), French (fr),
 * Portuguese (pt), Swahili (sw), Zulu (zu). Others can be added by dropping
 * new JSON files under src/i18n/locales/<lang>/ and listing them in
 * SUPPORTED_LANGUAGES.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import snCommon from './locales/sn/common.json';
import ndCommon from './locales/nd/common.json';
import frCommon from './locales/fr/common.json';
import ptCommon from './locales/pt/common.json';
import swCommon from './locales/sw/common.json';
import zuCommon from './locales/zu/common.json';

// Legacy translation packs (events / scanner / check-in / dashboard / auth /
// errors / etc.) — preserved verbatim from the old `src/lib/i18n.js`. Merged
// into the SAME `common` namespace so callers like `t('events.title')`,
// `t('scanner.tapToScan')`, `t('dashboard.welcome')` continue to resolve
// without code changes anywhere downstream.
import enLegacy from './locales/en/legacy.json';
import snLegacy from './locales/sn/legacy.json';
import ndLegacy from './locales/nd/legacy.json';
import frLegacy from './locales/fr/legacy.json';
import ptLegacy from './locales/pt/legacy.json';
import swLegacy from './locales/sw/legacy.json';
import zuLegacy from './locales/zu/legacy.json';

// Deep merge: keys in the second argument win. We want new common.json keys
// (e.g. nav.dashboard with the modern student-portal wording) to override
// any legacy duplicates, so legacy is the BASE and common.json is overlaid.
const deepMerge = (base, overlay) => {
  if (!base || typeof base !== 'object') return overlay;
  if (!overlay || typeof overlay !== 'object') return base;
  const out = { ...base };
  for (const k of Object.keys(overlay)) {
    out[k] = (k in base && typeof base[k] === 'object' && !Array.isArray(base[k]))
      ? deepMerge(base[k], overlay[k])
      : overlay[k];
  }
  return out;
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',    name: 'English',    nativeName: 'English',    flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  { code: 'sn', label: 'Shona',      name: 'Shona',      nativeName: 'chiShona',   flag: '\uD83C\uDDFF\uD83C\uDDFC' },
  { code: 'nd', label: 'Ndebele',    name: 'Ndebele',    nativeName: 'isiNdebele', flag: '\uD83C\uDDFF\uD83C\uDDFC' },
  { code: 'fr', label: 'Français',   name: 'French',     nativeName: 'Français',   flag: '\uD83C\uDDEB\uD83C\uDDF7' },
  { code: 'pt', label: 'Português',  name: 'Portuguese', nativeName: 'Português',  flag: '\uD83C\uDDF5\uD83C\uDDF9' },
  { code: 'sw', label: 'Kiswahili',  name: 'Swahili',    nativeName: 'Kiswahili',  flag: '\uD83C\uDDF0\uD83C\uDDEA' },
  { code: 'zu', label: 'isiZulu',    name: 'Zulu',       nativeName: 'isiZulu',    flag: '\uD83C\uDDFF\uD83C\uDDE6' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: deepMerge(enLegacy, enCommon) },
      sn: { common: deepMerge(snLegacy, snCommon) },
      nd: { common: deepMerge(ndLegacy, ndCommon) },
      fr: { common: deepMerge(frLegacy, frCommon) },
      pt: { common: deepMerge(ptLegacy, ptCommon) },
      sw: { common: deepMerge(swLegacy, swCommon) },
      zu: { common: deepMerge(zuLegacy, zuCommon) },
    },
    fallbackLng: 'en',
    lng: 'en',
    supportedLngs: ['en', 'sn', 'nd', 'fr', 'pt', 'sw', 'zu'],
    defaultNS: 'common',
    ns: ['common'],
    keySeparator: '.',
    nsSeparator: ':',
    debug: false,
    react: { useSuspense: false },
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

if (typeof window !== 'undefined') {
  // Expose for debugging — lets us inspect resource bundles in DevTools.
  window.__i18n = i18n;
  // Sanity check at boot: log when the namespace doesn't actually contain
  // our expected nav.* keys so we catch a broken JSON shape early.
  // eslint-disable-next-line no-console
  console.log('[i18n] resource keys:', Object.keys(i18n.getResourceBundle('en', 'common') || {}));
}

export default i18n;
